import React, {createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {inAfter} from "./fly";
// zed-sim.js is a UMD file (the playground loads it as a plain script). Depending on how the bundler treats it,
// it lands on module.exports or on window.
const zedSimModule = require("./zed-sim");
const ZedSim = zedSimModule.createZed ? zedSimModule : (zedSimModule.default || window.ZedSim);

// Zed's real face on a phone, as the face playground draws him, driven by the same simulation as the show
// (zed-sim.js). The drawing is ported from ~/workspace/madam/face/mouth-playground.html (render, drawEyes and
// its face markup), minus the hardware bridge, mustaches, pinwheel and stage lighting. `line` types out one letter at a time while his mouth shapes the letters.
const GRID_W = 64, GRID_H = 32;
const CHAR_MS = 55;
// He breathes between sentences (and a little at commas) instead of typing straight through.
const SENTENCE_PAUSE_MS = 380, COMMA_PAUSE_MS = 150;
// When each character appears, in seconds.
const typingTimes = (chars) => {
    let t = 0;
    return chars.map((c, i) => {
        const at = t / 1000;
        const next = chars[i + 1];
        t += CHAR_MS;
        if (next === ' ' && /[.!?]/.test(c)) t += SENTENCE_PAUSE_MS;
        else if (next === ' ' && c === ',') t += COMMA_PAUSE_MS;
        return at;
    });
};

const mouthPath = (ctx, cx, cy, hw, top, bottom) => {
    ctx.beginPath();
    ctx.moveTo(cx - hw, cy);
    ctx.quadraticCurveTo(cx, cy - 2 * top, cx + hw, cy);
    ctx.quadraticCurveTo(cx, cy + 2 * bottom, cx - hw, cy);
    ctx.closePath();
};

const teethCrescent = (ctx, cx, cy, h, amp, spanFrac, thick, dir) => {
    const s = spanFrac * h;
    if (s <= 0 || h <= 0) return;
    const q = (s / h) ** 2;
    const yEnd = amp * (1 - q), cOut = amp * (1 + q), cIn = 2 * (amp + dir * thick) - yEnd;
    ctx.beginPath();
    ctx.moveTo(cx - s, cy + yEnd);
    ctx.quadraticCurveTo(cx, cy + cOut, cx + s, cy + yEnd);
    ctx.quadraticCurveTo(cx, cy + cIn, cx - s, cy + yEnd);
    ctx.closePath();
    ctx.fill();
};

const canvas = (w, h) => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; };


const FACE_W = 600;

const createRenderer = (P) => {
    const panel = canvas(768, 384);
    const eyes = [canvas(240, 240), canvas(240, 240)];
    const hi = canvas(GRID_W * 8, GRID_H * 8), hictx = hi.getContext('2d', {willReadFrequently: true});
    const hi2 = canvas(GRID_W * 8, GRID_H * 8), hi2ctx = hi2.getContext('2d', {willReadFrequently: true});
    const lo = canvas(GRID_W, GRID_H), loctx = lo.getContext('2d', {willReadFrequently: true});
    const lo2 = canvas(GRID_W, GRID_H), lo2ctx = lo2.getContext('2d', {willReadFrequently: true});

    const drawMouth = (E) => {
        const pctx = panel.getContext('2d');
        hictx.setTransform(1, 0, 0, 1, 0, 0);
        hictx.fillStyle = '#000';
        hictx.fillRect(0, 0, hi.width, hi.height);
        hictx.setTransform(8, 0, 0, 8, 0, 0);
        hictx.fillStyle = E.fieldColor;
        hictx.beginPath();
        hictx.ellipse(E.fieldCX, E.fieldCY, E.fieldRX, E.fieldRY, 0, 0, Math.PI * 2);
        hictx.fill();
        mouthPath(hictx, E.mouthCX, E.mouthCY, E.mouthHW, E.topDepth, E.bottomDepth);
        hictx.fillStyle = '#000';
        hictx.fill();

        hi2ctx.setTransform(1, 0, 0, 1, 0, 0);
        hi2ctx.clearRect(0, 0, hi2.width, hi2.height);
        hi2ctx.setTransform(8, 0, 0, 8, 0, 0);
        hi2ctx.save();
        mouthPath(hi2ctx, E.mouthCX, E.mouthCY, E.mouthHW, E.topDepth, E.bottomDepth);
        hi2ctx.clip();
        hi2ctx.fillStyle = '#fff';
        const inset = E.teethInset, hIn = E.mouthHW - inset;
        if (E.teethTop) teethCrescent(hi2ctx, E.mouthCX, E.mouthCY, hIn, -(E.topDepth - inset), E.topSpan, E.topThick, 1);
        if (E.teethBottom) teethCrescent(hi2ctx, E.mouthCX, E.mouthCY, hIn, E.bottomDepth - inset, E.bottomSpan, E.bottomThick, -1);
        hi2ctx.restore();

        loctx.imageSmoothingQuality = 'high';
        loctx.clearRect(0, 0, GRID_W, GRID_H);
        loctx.drawImage(hi, 0, 0, GRID_W, GRID_H);
        const px = loctx.getImageData(0, 0, GRID_W, GRID_H).data;
        lo2ctx.imageSmoothingQuality = 'high';
        lo2ctx.clearRect(0, 0, GRID_W, GRID_H);
        lo2ctx.drawImage(hi2, 0, 0, GRID_W, GRID_H);
        const px2 = lo2ctx.getImageData(0, 0, GRID_W, GRID_H).data;

        pctx.fillStyle = '#000';
        pctx.fillRect(0, 0, panel.width, panel.height);
        const CELL = panel.width / GRID_W, r = CELL * P.dotScale, fc = E.fieldColor;
        const fMax = Math.max(parseInt(fc.slice(1, 3), 16), parseInt(fc.slice(3, 5), 16), parseInt(fc.slice(5, 7), 16)) || 255;
        for (let y = 0; y < GRID_H; y++) {
            for (let x = 0; x < GRID_W; x++) {
                const i = (y * GRID_W + x) * 4;
                const R = px[i], G = px[i + 1], B = px[i + 2];
                if (px2[i + 3] >= P.teethThresh * 255) {
                    pctx.fillStyle = P.teethColor;
                } else if (Math.max(R, G, B) / fMax >= P.fieldThresh) {
                    const d = Math.hypot(x + 0.5 - E.mouthCX, y + 0.5 - E.mouthCY);
                    const sh = 1 - P.fieldShade * 0.6 * Math.min(1, Math.max(0, (d - 10) / 26));
                    pctx.fillStyle = `rgb(${R * sh | 0},${G * sh | 0},${B * sh | 0})`;
                } else continue;
                pctx.beginPath();
                pctx.arc((x + 0.5) * CELL, (y + 0.5) * CELL, r, 0, Math.PI * 2);
                pctx.fill();
            }
        }
    };

    const drawEyes = (eye) => {
        const {gx, gy, lid, pupil} = eye;
        const sqx = 1 - 0.22 * (gx / 64) ** 2, open = 1 - lid, TAU = Math.PI * 2;
        eyes.forEach((cv, i) => {
            const ctx = cv.getContext('2d'), S = 240, c = S / 2;
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, S, S);
            const w = P.eyeW, top = P.eyeTop * open * eye.uo, bot = P.eyeBot * open * eye.lo;
            ctx.beginPath();
            ctx.moveTo(c - w, c);
            ctx.quadraticCurveTo(c, c - 2 * top, c + w, c);
            ctx.quadraticCurveTo(c, c + 2 * bot, c - w, c);
            ctx.closePath();
            ctx.fillStyle = P.scleraColor;
            ctx.fill();
            ctx.save();
            ctx.clip();
            const ix = c + gx + (i === 0 ? 3 : -3), iy = c + gy;
            if (P.irisSize > 0) {
                ctx.fillStyle = P.irisColor;
                ctx.beginPath(); ctx.ellipse(ix, iy, P.irisSize * sqx, P.irisSize, 0, 0, TAU); ctx.fill();
            }
            ctx.fillStyle = '#050303';
            ctx.beginPath();
            ctx.ellipse(ix, iy, pupil * sqx, pupil * P.pupilStretch, 0, 0, TAU);
            ctx.fill();
            ctx.restore();
        });
    };

    return (target, s) => {
        drawEyes(s.eye);
        drawMouth(s.E);
        drawFace(target.getContext('2d'), s, panel, eyes);
    };
};

// The whole face on one canvas (design size FACE_W × FACE_H), laid out like the playground page: brows over
// round display eyes with a brass nose between them, the LED mouth panel below.
const FACE_H = 600;
// The face's own bounds within that design space (brows to the bottom of the mouth panel, the panel's width):
// the canvas shows just this, so he fills his spot without empty margin.
const CROP = {x: 42, y: -24, w: 516, h: 568};
// The top HEADROOM of the canvas is only for brows tilted up (expressive ones go well past 20°). It hangs
// above the face's slot instead of taking layout space, so it overlaps the box's padding.
const HEADROOM = 40;

// His section of the box is cardboard, like his real body, flush to the box's edges.
const cardboard = {
    background: 'radial-gradient(120% 90% at 30% 20%, #3b2d1f, #2a2017 70%, #211912)',
    boxShadow: 'inset 0 0 16px rgba(0,0,0,0.5)',
}

// His face in a slot of the given width: the slot is sized to the face, the canvas overflows it upward.
const FaceSlot = ({canvasRef, width}) => (
    <div style={{position: 'relative', width, aspectRatio: `${CROP.w} / ${CROP.h - HEADROOM}`, flexShrink: 0}}>
        <canvas ref={canvasRef} width={CROP.w} height={CROP.h}
                style={{position: 'absolute', left: 0, bottom: 0, width: '100%', height: 'auto'}} />
    </div>
);
const EYE_R = 72, EYE_Y = 150, EYE_DX = 116;

const brassFill = (c, y0, y1) => {
    const g = c.createLinearGradient(0, y0, 0, y1);
    g.addColorStop(0, '#8a6a3d'); g.addColorStop(1, '#5d4526');
    return g;
};

const drawFace = (c, s, panel, eyes) => {
    const cx = FACE_W / 2;
    c.setTransform(1, 0, 0, 1, -CROP.x, -CROP.y);
    c.clearRect(CROP.x, CROP.y, CROP.w, CROP.h);
    // the LED mouth panel, bare
    const mw = 508, mh = 254;
    c.drawImage(panel, cx - mw / 2, 284, mw, mh);
    // nose
    c.fillStyle = brassFill(c, 196, 236);
    c.beginPath(); c.roundRect(cx - 26, 198, 52, 40, 9); c.fill();
    // eyes + brows
    [[-1, s.bL, 0], [1, -s.bR, 1]].forEach(([side, ang, i]) => {
        const ex = cx + side * EYE_DX;
        c.save();
        c.beginPath(); c.arc(ex, EYE_Y, EYE_R + 9, 0, Math.PI * 2);
        c.fillStyle = '#7a5c33'; c.fill();
        c.beginPath(); c.arc(ex, EYE_Y, EYE_R, 0, Math.PI * 2); c.clip();
        c.drawImage(eyes[i], ex - EYE_R, EYE_Y - EYE_R, EYE_R * 2, EYE_R * 2);
        const gl = c.createRadialGradient(ex - 24, EYE_Y - 30, 4, ex - 24, EYE_Y - 30, 62);
        gl.addColorStop(0, 'rgba(255,255,255,0.32)'); gl.addColorStop(0.4, 'rgba(255,255,255,0.06)'); gl.addColorStop(0.7, 'rgba(255,255,255,0)');
        c.fillStyle = gl; c.fillRect(ex - EYE_R, EYE_Y - EYE_R, EYE_R * 2, EYE_R * 2);
        c.restore();
        c.save();
        c.translate(ex - side * 8, EYE_Y - EYE_R - 24);
        c.rotate(ang * Math.PI / 180);
        c.fillStyle = brassFill(c, -13, 13);
        c.beginPath(); c.roundRect(-82, -13, 164, 26, 5); c.fill();
        c.fillStyle = '#3d2c17';
        [-68, 68].forEach(rx => { c.beginPath(); c.arc(rx, 0, 4, 0, Math.PI * 2); c.fill(); });
        c.restore();
    });
};

// <ZedSays line="What is your name?" onDone={...} />: a game-style dialog box, his face on the left and the
// line typing out on the right in LED type. `speech` is for long paragraphs (the verdict): face on top,
// readable text below.
// The cursor hangs off an empty inline span: no width, and no place for the line to break.
const Cursor = ({on}) => <span style={{position: 'relative'}}>
    <span style={{position: 'absolute', left: 0, top: 0, opacity: on ? 1 : 0}}>█</span>
</span>;

// The whole line is laid out from the start, the untyped rest invisible, so words never jump lines mid-typing.
const renderLine = (plain, shown, typing) => <>
    {plain.slice(0, shown).join('')}
    <Cursor on={typing} />
    <span style={{visibility: 'hidden'}}>{plain.slice(shown).join('')}</span>
</>;

// A tiny tick per letter as he types. Android vibrates; iPhones can't from a web page, except that iOS 18+
// gives a system tick when a switch control toggles, so a hidden one is clicked.
let tickSwitch = null;
const tick = () => {
    if (navigator.vibrate) { navigator.vibrate(4); return; }
    if (!tickSwitch) {
        const label = document.createElement('label');
        label.style.cssText = 'position:fixed;left:-100px;top:0;opacity:0;pointer-events:none';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.setAttribute('switch', '');
        label.appendChild(input);
        document.body.appendChild(label);
        tickSwitch = label;
    }
    tickSwitch.click();
};

// The LED box holds four lines at most; a longer line shrinks its type until the whole thing fits.
const LED_MAX = 31, LED_MIN = 16, LED_BOX_H = 132;

// Wait for the LED font before typing, so letters don't change width mid-line when it arrives.
let ledFontReady = null;
const loadLedFont = () => {
    if (!ledFontReady) {
        ledFontReady = Promise.race([
            document.fonts ? document.fonts.load(`900 ${LED_MAX}px Doto`).catch(() => {}) : Promise.resolve(),
            new Promise(r => setTimeout(r, 1500)),
        ]);
    }
    return ledFontReady;
};

const ZedSaysBox = ({line, onDone, speech = false}) => {
    const canvasRef = useRef(null);
    const zedRef = useRef(null);
    const textBoxRef = useRef(null);
    const [shown, setShown] = useState(0);
    const [fontReady, setFontReady] = useState(false);
    const [fontSize, setFontSize] = useState(LED_MAX);

    const plain = useMemo(() => [...(line || '')], [line]);

    useEffect(() => { loadLedFont().then(() => setFontReady(true)); }, []);

    // Fit the whole line (it's all laid out, the untyped rest invisible) before typing starts.
    useLayoutEffect(() => {
        if (speech || !textBoxRef.current) return;
        const boxEl = textBoxRef.current;
        let size = LED_MAX;
        boxEl.style.fontSize = `${size}px`;
        // Compare against the box's limit, not the element's own height: the LED glyphs poke a few pixels past
        // their line, so a line that fits still reports a little overflow.
        // And never split a word: if the longest one is wider than the box, the whole line steps down.
        while (size > LED_MIN && (boxEl.scrollHeight > LED_BOX_H + 4 || boxEl.scrollWidth > boxEl.clientWidth + 3)) {
            size -= 1;
            boxEl.style.fontSize = `${size}px`;
        }
        setFontSize(size);
    }, [line, fontReady, speech]);

    useEffect(() => {
        const P = Object.assign({}, ZedSim.DEFAULT_P);
        const zed = ZedSim.createZed({P});
        zedRef.current = zed;
        const draw = createRenderer(P);
        let raf;
        const tick = (now) => {
            const s = zed.step(now);
            if (canvasRef.current) draw(canvasRef.current, s);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    useEffect(() => {
        const zed = zedRef.current;
        if (!zed || !line || (!fontReady && !speech)) return undefined;
        const chars = plain;
        const times = typingTimes(chars);
        const start = performance.now();
        zed.say({cues: ZedSim.directCues(chars, times), time: () => (performance.now() - start) / 1000});
        setShown(0);
        let done = false;
        const id = setInterval(() => {
            const elapsed = (performance.now() - start) / 1000;
            let n = 0;
            while (n < chars.length && times[n] <= elapsed) n++;
            setShown(prev => {
                if (n > prev && chars[n - 1] && chars[n - 1].trim()) tick();
                return n;
            });
            if (n >= chars.length && !done) {
                done = true;
                clearInterval(id);
                setTimeout(() => { zed.endLine(); onDone && onDone(); }, 250);
            }
        }, 20);
        return () => clearInterval(id);
    }, [line, fontReady]); // eslint-disable-line react-hooks/exhaustive-deps

    const typing = shown < plain.length;
    const text = <span>{renderLine(plain, shown, typing)}</span>;
    const box = {borderRadius: 16, width: '100%', boxSizing: 'border-box',
        background: 'linear-gradient(#181410, #0a0807)', position: 'relative', zIndex: 1,
        boxShadow: 'inset 0 1px 0 rgba(255,230,190,0.08), 0 10px 30px rgba(0,0,0,0.55), var(--zed-rim, 0 0 0 0 transparent)'};

    if (speech) return <div style={{...box, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
        <div style={{...cardboard, display: 'flex', justifyContent: 'center', padding: '16px 18px 12px'}}>
            <FaceSlot canvasRef={canvasRef} width="46%" />
        </div>
        <div style={{padding: '12px 18px 20px', fontSize: 21, fontWeight: 600, lineHeight: 1.4, color: '#f3ead8', minHeight: '8.4em'}}>{text}</div>
    </div>;

    return <div style={{...box, overflow: 'hidden', display: 'flex', alignItems: 'stretch'}}>
        <div style={{...cardboard, width: 'calc(42% + 9px)', boxSizing: 'border-box', padding: '16px 10px', flexShrink: 0,
            display: 'flex', alignItems: 'center'}}>
            <FaceSlot canvasRef={canvasRef} width="100%" />
        </div>
        <div style={{flex: 1, minWidth: 0, height: LED_BOX_H, display: 'flex', alignItems: 'center', padding: '16px 16px 16px 12px', boxSizing: 'content-box'}}>
            {/* text-size-adjust: Android Chrome can enlarge text on its own, which would undo the fit */}
            <div ref={textBoxRef} style={{width: '100%', maxHeight: LED_BOX_H, overflow: 'hidden', fontFamily: "'Doto', monospace",
                WebkitTextSizeAdjust: '100%', textSizeAdjust: '100%',
                fontSize, fontWeight: 900, lineHeight: 1.05, textTransform: 'uppercase', letterSpacing: 1, color: '#ff3d14',
                overflowWrap: 'normal', textShadow: '0 0 6px rgba(255,61,20,0.85), 0 0 16px rgba(255,43,10,0.45)'}}>
                {text}
            </div>
        </div>
    </div>;
};

// <ZedVoiced chars times audio />: the verdict as the host's phone plays it. His mouth and the typed words both
// follow the audio's own clock (ElevenLabs' per-character start times), one paragraph at a time, so they can't
// drift from his voice. Before `audio` starts he just idles with the first paragraph's space held.
export const ZedVoiced = ({chars, times, audio}) => {
    const canvasRef = useRef(null);
    const zedRef = useRef(null);
    const [shown, setShown] = useState(0);

    const text = chars.join('');
    // paragraph boundaries in character offsets
    const paras = [];
    let start = 0;
    text.split(/(\n+)/).forEach(part => {
        if (/^\n+$/.test(part)) { start += part.length; return; }
        if (part.trim()) paras.push({start, end: start + part.length});
        start += part.length;
    });

    useEffect(() => {
        const P = Object.assign({}, ZedSim.DEFAULT_P);
        const zed = ZedSim.createZed({P});
        zedRef.current = zed;
        const draw = createRenderer(P);
        let raf;
        const tick = (now) => {
            const s = zed.step(now);
            if (canvasRef.current) draw(canvasRef.current, s);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    useEffect(() => {
        const zed = zedRef.current;
        if (!audio || !zed) return undefined;
        zed.say({cues: ZedSim.directCues(chars, times), time: () => audio.currentTime});
        const id = setInterval(() => {
            const t = audio.currentTime;
            let n = 0;
            while (n < times.length && times[n] <= t) n++;
            setShown(n);
        }, 30);
        const end = () => { zed.endLine(); setShown(chars.length); };
        audio.addEventListener('ended', end);
        return () => { clearInterval(id); audio.removeEventListener('ended', end); };
    }, [audio]); // eslint-disable-line react-hooks/exhaustive-deps

    // A paragraph stays up, finished, until his voice reaches the first letter of the next one.
    const para = [...paras].reverse().find(p => p.start < shown) || paras[0] || {start: 0, end: 0};
    const typed = text.slice(para.start, Math.max(para.start, Math.min(shown, para.end)));
    const rest = text.slice(para.start + typed.length, para.end);
    return <div style={{borderRadius: 16, width: '100%', boxSizing: 'border-box', position: 'relative', zIndex: 1, overflow: 'hidden',
        background: 'linear-gradient(#181410, #0a0807)', boxShadow: 'inset 0 1px 0 rgba(255,230,190,0.08), 0 10px 30px rgba(0,0,0,0.55), var(--zed-rim, 0 0 0 0 transparent)',
        display: 'flex', flexDirection: 'column'}}>
        <div style={{...cardboard, display: 'flex', justifyContent: 'center', padding: '16px 18px 12px'}}>
            <FaceSlot canvasRef={canvasRef} width="46%" />
        </div>
        <div style={{padding: '12px 18px 20px', fontSize: 21, fontWeight: 600, lineHeight: 1.4, color: '#f3ead8', minHeight: '8.4em'}}>
            <span>{typed}<span style={{visibility: 'hidden'}}>{rest}</span></span>
        </div>
    </div>;
};

// One Zed across screens (the tour, for now): a ZedHost renders a single box that stays alive between steps;
// each screen's <ZedSays> becomes a slot that tells the host where the box sits and what he says, and the box
// glides there. Outside a host, <ZedSays> renders its own box as before.
const ZedHostContext = createContext(null);

export const ZedSays = (props) => {
    const host = useContext(ZedHostContext);
    if (!host) return <ZedSaysBox {...props} />;
    return <ZedSlot host={host} {...props} />;
};

// `at`: the slot moved without its line changing (say, the screen's layout shifted); a new value re-measures it, so the
// box glides to the new spot.
const ZedSlot = ({host, line, onDone, speech = false, at}) => {
    const ref = useRef(null);
    const onDoneRef = useRef(onDone);
    onDoneRef.current = onDone;
    const hostNow = useRef(host);
    hostNow.current = host;
    useLayoutEffect(() => {
        if (host.exiting) return undefined;
        const report = () => ref.current && host.place(ref.current, {line, speech, onDone: () => onDoneRef.current && onDoneRef.current()});
        report();
        const ro = new ResizeObserver(report);
        ro.observe(ref.current);
        window.addEventListener('resize', report);
        return () => { ro.disconnect(); window.removeEventListener('resize', report); };
    }, [line, speech, host.exiting, at]); // eslint-disable-line react-hooks/exhaustive-deps
    // counted while on an active screen; when none have a place for him (e.g. the host's voiced verdict has its own
    // face), the box leaves
    useLayoutEffect(() => (host.exiting ? undefined : host.register()), [host.exiting]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => () => hostNow.current.release(), []);
    return <div ref={ref} data-zed-slot="" style={{width: '100%', height: host.boxHeight[speech ? 'speech' : 'compact'] || 160, flexShrink: 0}} />;
};

// Marks a screen that's leaving: its <ZedSays> stops steering the shared box.
export const ZedLeaving = ({active, children}) => {
    const host = useContext(ZedHostContext);
    const value = useMemo(() => (host && active ? {...host, place: () => {}, release: () => {}, register: () => undefined, exiting: true} : host), [host, active]);
    // the screen leaving hands the box back; it stays only if the screen arriving has a place for it
    useEffect(() => { if (active && host) host.release(); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
    return <ZedHostContext.Provider value={value}>{children}</ZedHostContext.Provider>;
};

export const ZedHost = ({children}) => {
    const hostRef = useRef(null);
    const boxRef = useRef(null);
    const [spot, setSpot] = useState(null); // {top, left, width, line, speech, onDone}
    const [boxHeight, setBoxHeight] = useState({});

    const slots = useRef(0); // slots on active screens
    // his first moments on screen: corrections as the box measures itself and the font loads snap instead of gliding
    const born = useRef(0);
    if (spot && !born.current) born.current = performance.now();
    const settled = born.current && performance.now() - born.current > 700;
    const place = (slotEl, said) => {
        // a slot's first report can arrive before the host itself is mounted: try again next frame
        if (!hostRef.current) { requestAnimationFrame(() => place(slotEl, said)); return; }
        const h = hostRef.current.getBoundingClientRect(), r = slotEl.getBoundingClientRect();
        setSpot(prev => {
            const next = {top: r.top - h.top, left: r.left - h.left, width: r.width, ...said};
            return prev && prev.top === next.top && prev.left === next.left && prev.width === next.width &&
                prev.line === next.line && prev.speech === next.speech ? prev : next;
        });
    };

    useEffect(() => {
        if (!boxRef.current || !spot) return undefined;
        const measure = () => boxRef.current && setBoxHeight(b => {
            const key = spot.speech ? 'speech' : 'compact', hgt = Math.round(boxRef.current.offsetHeight);
            return b[key] === hgt ? b : {...b, [key]: hgt};
        });
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(boxRef.current);
        return () => ro.disconnect();
    }, [spot && spot.speech]); // eslint-disable-line react-hooks/exhaustive-deps

    // Switching between his compact box and the verdict layout is a different box, so it can't glide: the old one
    // flies off like any other piece and the new one flies in where it belongs. When no screen has a place for
    // him, the box flies off; when one does again, it flies back in.
    const shape = spot && (spot.speech ? 'speech' : 'compact');
    const last = useRef(null);
    const gen = useRef(0); // a box that left and came back is a new box
    const [gone, setGone] = useState(null);
    const [arriving, setArriving] = useState(false);
    const swapTimer = useRef(null);
    useLayoutEffect(() => {
        const prev = last.current;
        const settle = () => {
            clearTimeout(swapTimer.current);
            swapTimer.current = setTimeout(() => { setGone(null); setArriving(false); }, inAfter() + 900);
        };
        if (!spot) {
            if (prev) { setGone(prev); setArriving(false); settle(); gen.current++; }
            last.current = null;
            return;
        }
        if (!prev && gen.current > 0) { setArriving(inAfter()); settle(); }
        if (prev && prev.shape !== shape) { setGone(prev); setArriving(inAfter()); settle(); }
        last.current = {...spot, shape, key: `${shape}-${gen.current}`};
    }, [spot]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => () => clearTimeout(swapTimer.current), []);

    const boxes = [];
    const currentKey = spot && `${shape}-${gen.current}`;
    if (gone && gone.key !== currentKey) boxes.push({...gone, mode: 'out'});
    if (spot) boxes.push({...spot, shape, key: currentKey, mode: arriving ? 'in' : 'still'});

    const register = () => {
        slots.current++;
        return () => { slots.current--; };
    };
    const release = () => requestAnimationFrame(() => { if (slots.current === 0) setSpot(null); });

    const value = useMemo(() => ({place, release, register, boxHeight, exiting: false}), [boxHeight]); // eslint-disable-line react-hooks/exhaustive-deps
    return <div ref={hostRef} style={{position: 'relative'}}>
        <ZedHostContext.Provider value={value}>{children}</ZedHostContext.Provider>
        {boxes.map(b => <div key={b.key} ref={b.mode === 'out' ? undefined : boxRef} className={b.mode === 'still' ? undefined : `fly-${b.mode}`}
            style={{position: 'absolute', top: b.top, left: b.left, width: b.width, zIndex: 5, pointerEvents: b.mode === 'out' ? 'none' : undefined,
                '--delay': `${b.mode === 'in' ? arriving + 100 : 0}ms`, '--dur': '750ms', '--rot': '0deg', '--dy': '0px',
                transition: b.mode === 'still' && settled ? 'top 750ms cubic-bezier(0.3, 0.9, 0.3, 1) 250ms, left 750ms cubic-bezier(0.3, 0.9, 0.3, 1) 250ms, width 750ms ease 250ms' : 'none'}}>
            <ZedSaysBox line={b.line} speech={b.speech} onDone={b.mode === 'out' ? undefined : b.onDone} />
        </div>)}
    </div>;
};
